import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { determinarObligaciones, calcularMuestraGIII, calificarGuiaI, calificarGuiaII, calificarGuiaIII } from '../lib/scoring';
import { informeFueGenerado } from '../lib/api';
import { COLORES_NIVEL } from '../data';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const NIV_NAMES = ['Nulo','Bajo','Medio','Alto','Muy alto'];

export default function DashboardPage() {
  const { state } = useAppState();
  const navigate = useNavigate();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;
  const centro = state.centrosTrabajo[state.centroActualIdx];
  if (!centro) return null;

  const trabajadores = centro.trabajadores || [];
  const cuestionarios = centro.cuestionariosCompletos || {};
  const fichas = centro.fichas || {};
  const consentimientos = centro.consentimientos || {};
  const obligaciones = determinarObligaciones(centro.numTrabajadores);
  const muestra = calcularMuestraGIII(centro.numTrabajadores);

  const stats = useMemo(() => {
    const totalTrab = trabajadores.length;
    const fichasCount = Object.keys(fichas).length;
    const consentCount = Object.keys(consentimientos).length;
    const giCount = trabajadores.filter(tw => cuestionarios[`${tw.id}_guiaI`]).length;
    const giiCount = trabajadores.filter(tw => cuestionarios[`${tw.id}_guiaII`]).length;
    const giiiCount = trabajadores.filter(tw => cuestionarios[`${tw.id}_guiaIII`]).length;
    return { totalTrab, fichasCount, consentCount, giCount, giiCount, giiiCount };
  }, [trabajadores, cuestionarios, fichas, consentimientos]);

  // ── Resultados para gráficas ────────────────────────────
  const { riskDistribution, giSummary, domainData } = useMemo(() => {
    const dist = { 'Nulo': 0, 'Bajo': 0, 'Medio': 0, 'Alto': 0, 'Muy alto': 0 };
    let giOk = 0, giATS = 0, giTotal = 0;
    const domAgg = {};

    trabajadores.forEach(tw => {
      const r1 = cuestionarios[`${tw.id}_guiaI`];
      if (r1) { giTotal++; const res = calificarGuiaI(r1); if (res.requiereAtencion) giATS++; else giOk++; }

      const r2 = cuestionarios[`${tw.id}_guiaII`];
      if (r2) {
        const res = calificarGuiaII(r2);
        dist[res.nivelFinal]++;
        Object.entries(res.dominios).forEach(([dom, data]) => {
          if (!domAgg[dom]) domAgg[dom] = { 'Nulo': 0, 'Bajo': 0, 'Medio': 0, 'Alto': 0, 'Muy alto': 0 };
          domAgg[dom][data.nivel]++;
        });
      }
      const r3 = cuestionarios[`${tw.id}_guiaIII`];
      if (r3) {
        const res = calificarGuiaIII(r3);
        dist[res.nivelFinal]++;
        Object.entries(res.dominios).forEach(([dom, data]) => {
          if (!domAgg[dom]) domAgg[dom] = { 'Nulo': 0, 'Bajo': 0, 'Medio': 0, 'Alto': 0, 'Muy alto': 0 };
          domAgg[dom][data.nivel]++;
        });
      }
    });

    const riskDistribution = NIV_NAMES.map(n => ({ name: n, value: dist[n], color: COLORES_NIVEL[n] })).filter(d => d.value > 0);
    const giSummary = giTotal > 0 ? { ok: giOk, ats: giATS, total: giTotal } : null;
    const domainData = Object.entries(domAgg).map(([dom, counts]) => {
      const short = dom.length > 22 ? dom.substring(0, 20) + '..' : dom;
      return { name: short, fullName: dom, ...counts };
    }).sort((a, b) => (b['Alto'] + b['Muy alto']) - (a['Alto'] + a['Muy alto']));

    return { riskDistribution, giSummary, domainData };
  }, [trabajadores, cuestionarios]);

  const hayResultados = riskDistribution.length > 0 || giSummary;

  // ── Steps checklist ─────────────────────────────────────
  const steps = [
    { label: '1. Registrar política de prevención', done: state.politicaData.guardado, go: '/politica' },
    { label: '2. Dar de alta trabajadores', done: stats.totalTrab > 0, go: '/trabajadores' },
    { label: '3. Obtener consentimiento informado', done: stats.consentCount > 0, go: '/cuestionarios' },
    { label: '4. Completar fichas (Guía V)', done: stats.fichasCount > 0, go: '/cuestionarios' },
    { label: '5. Aplicar cuestionarios', done: stats.giCount > 0, go: '/cuestionarios' },
    { label: '6. Revisar resultados', done: hayResultados, go: '/resultados' },
    { label: '7. Programa de intervención', done: ((centro?.programaIntervencion?.acciones) || []).length > 0, go: '/programa' },
    { label: '8. Generar informe PDF', done: informeFueGenerado(centro.id), go: '/resultados' },
  ];
  const completados = steps.filter(s => s.done).length;
  const progreso = Math.round((completados / steps.length) * 100);

  const cardStyle = { padding: 20, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14 };
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return <div style={{ background: dark ? '#1a1d27' : '#fff', border: `1px solid ${t.border}`, borderRadius: 8, padding: '8px 12px', fontSize: '0.78rem' }}>
      {payload.map((p, i) => <div key={i} style={{ color: p.color || p.fill, fontWeight: 600 }}>{p.name}: {p.value}</div>)}
    </div>;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>{centro.nombreCentro}</h1>
          <p style={{ color: t.textMuted, margin: '4px 0 0', fontSize: '0.88rem' }}>{centro.numTrabajadores} trabajadores — {state.empresa.nombre}</p>
        </div>
        <div style={{ padding: '6px 14px', background: t.accentLight, color: t.textLink, borderRadius: 8, fontSize: '0.8rem', fontWeight: 600 }}>
          Nivel {obligaciones.nivel} — {obligaciones.desc}
        </div>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Trabajadores', value: stats.totalTrab, action: () => navigate('/trabajadores') },
          { label: 'Fichas', value: `${stats.fichasCount}/${stats.totalTrab}` },
          { label: 'Consentimientos', value: `${stats.consentCount}/${stats.totalTrab}` },
          { label: 'Guía I', value: `${stats.giCount}/${stats.totalTrab}` },
          ...(obligaciones.nivel >= 2 ? [{ label: 'Guía II', value: `${stats.giiCount}/${stats.totalTrab}` }] : []),
          ...(obligaciones.nivel >= 3 ? [{ label: 'Guía III', value: `${stats.giiiCount}/${muestra || stats.totalTrab}` }] : []),
        ].map(s => (
          <div key={s.label} style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: t.textMuted, marginBottom: 4, fontWeight: 500 }}>{s.label}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 700, color: t.textStrong }}>{s.value}</div>
            {s.action && <button onClick={s.action} style={{ background: 'none', border: 'none', color: t.textLink, fontSize: '0.75rem', cursor: 'pointer', padding: 0, marginTop: 4, fontWeight: 500 }}>Gestionar →</button>}
          </div>
        ))}
      </div>

      {/* Charts row */}
      {hayResultados && (
        <div style={{ display: 'grid', gridTemplateColumns: riskDistribution.length > 0 ? '280px 1fr' : '1fr', gap: 16, marginBottom: 24 }}>
          {/* Pie chart: risk distribution */}
          {riskDistribution.length > 0 && (
            <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong, marginBottom: 8, alignSelf: 'flex-start' }}>Distribución de riesgo</div>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={riskDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2} strokeWidth={0}>
                    {riskDistribution.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', justifyContent: 'center', marginTop: 4 }}>
                {riskDistribution.map(d => (
                  <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', color: t.textMuted }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                    {d.name}: {d.value}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bar chart: domains */}
          {domainData.length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong, marginBottom: 8 }}>Riesgo por dominio</div>
              <ResponsiveContainer width="100%" height={Math.max(200, domainData.length * 28 + 40)}>
                <BarChart data={domainData} layout="vertical" margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: t.textMuted }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 9, fill: t.textMuted }} />
                  <Tooltip content={<CustomTooltip />} />
                  {NIV_NAMES.map(n => <Bar key={n} dataKey={n} stackId="a" fill={COLORES_NIVEL[n]} radius={0} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* GI summary (if no GII/GIII) */}
          {riskDistribution.length === 0 && giSummary && (
            <div style={cardStyle}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong, marginBottom: 12 }}>Guía I — Resumen</div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1, padding: 16, background: 'rgba(16,185,129,0.08)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#10b981' }}>{giSummary.ok}</div>
                  <div style={{ fontSize: '0.75rem', color: t.textMuted }}>Sin atención requerida</div>
                </div>
                <div style={{ flex: 1, padding: 16, background: 'rgba(239,68,68,0.08)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#ef4444' }}>{giSummary.ats}</div>
                  <div style={{ fontSize: '0.75rem', color: t.textMuted }}>Requieren atención clínica</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Progress + Steps */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: t.textStrong, margin: 0 }}>Pasos para cumplir</h2>
          <span style={{ fontSize: '0.82rem', color: t.textLink, fontWeight: 600 }}>{progreso}%</span>
        </div>
        <div style={{ height: 6, background: dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ height: '100%', width: `${progreso}%`, background: t.accent, borderRadius: 3, transition: 'width 0.5s ease' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {steps.map((s, i) => (
            <button key={i} onClick={() => navigate(s.go)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 8, textAlign: 'left', color: s.done ? t.tagDone : t.textMuted, fontSize: '0.88rem' }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: s.done ? t.tagDone : (dark ? '#333' : '#d1d5db') }}>
                {s.done ? '✓' : i + 1}
              </div>
              <span style={{ fontWeight: s.done ? 500 : 400 }}>{s.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
